import { useState, useRef, useEffect } from 'react';
import type { Program } from '@/mocks/adminPrograms';
import { categoryLabels, categoryAccentColors } from '@/mocks/adminPrograms';
import { SwalAlert } from '@/hooks/useSweetAlert';

interface ProgramCardProps {
  program: Program;
  onEdit: (program: Program) => void;
  onShare: (program: Program) => void;
  onPromote: (program: Program) => void;
  onCommunity: (program: Program) => void;
  onStats: (program: Program) => void;
  onNotify: (program: Program) => void;
}

function formatNumber(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

export default function ProgramCard({ program, onEdit, onShare, onPromote, onCommunity, onStats, onNotify }: ProgramCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [menuOpen]);

  return (
    <div className="group bg-background-50 rounded-2xl border border-background-200/80 overflow-hidden hover:border-background-300/60 transition-all duration-300 hover:scale-[1.01]">
      {/* Portada */}
      <div className="relative w-full aspect-[16/10] overflow-hidden bg-background-200">
        {!imgError ? (
          <img
            src={program.imageUrl}
            alt={program.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-background-200">
            <i className="ri-mic-line text-foreground-300 text-4xl"></i>
          </div>
        )}
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

        {/* Status badge */}
        <div className="absolute top-3 left-3">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold ${
            program.isActive
              ? 'bg-emerald-500/90 text-background-50 backdrop-blur-sm'
              : 'bg-foreground-600/80 text-background-50 backdrop-blur-sm'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${program.isActive ? 'bg-background-50 animate-pulse' : 'bg-background-50/60'}`}></span>
            {program.isActive ? 'Al aire' : 'Pausado'}
          </span>
        </div>

        {/* Category badge */}
        <div className="absolute top-3 right-3">
          <span className={`inline-block px-2.5 py-1 rounded-lg text-[10px] font-bold backdrop-blur-sm ${categoryAccentColors[program.category]} text-background-50`}>
            {categoryLabels[program.category]}
          </span>
        </div>

        {/* Bottom info overlay */}
        <div className="absolute bottom-0 inset-x-0 p-4">
          <h3 className="text-lg font-bold text-background-50 leading-tight mb-1">{program.name}</h3>
          <p className="text-background-50/80 text-[11px] line-clamp-1">{program.schedule} · {program.days.slice(0, 2).join(', ')}{program.days.length > 2 ? '...' : ''}</p>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        {/* Host info */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-background-200 overflow-hidden shrink-0 ring-2 ring-background-100">
            <img
              src={program.hostPhotoUrl}
              alt={program.hostName}
              className="w-full h-full object-cover"
              onError={(e) => {
                const t = e.currentTarget;
                t.style.display = 'none';
                t.parentElement!.classList.add('flex', 'items-center', 'justify-center');
                t.parentElement!.innerHTML = '<i class="ri-user-line text-foreground-400 text-sm"></i>';
              }}
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground-900 truncate">{program.hostName}</p>
            {program.coHosts.length > 0 && (
              <p className="text-[11px] text-foreground-400 truncate">con {program.coHosts.join(', ')}</p>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-background-100/70">
            <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center shrink-0">
              <i className="ri-heart-line text-rose-500 text-sm"></i>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-foreground-900">{formatNumber(program.followers)}</p>
              <p className="text-[10px] text-foreground-400">Seguidores</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-background-100/70">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
              <i className="ri-flashlight-line text-amber-500 text-sm"></i>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-foreground-900">{formatNumber(program.interactions)}</p>
              <p className="text-[10px] text-foreground-400">Interacciones</p>
            </div>
          </div>
        </div>

        {/* Interaction breakdown */}
        <div className="flex items-center gap-3 text-[11px] text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-thumb-up-line text-foreground-400"></i>
            {formatNumber(program.likes)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-chat-3-line text-foreground-400"></i>
            {formatNumber(program.comments)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-share-forward-line text-foreground-400"></i>
            {formatNumber(program.shares)}
          </span>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1.5 pt-1 border-t border-background-100">
          <button
            onClick={() => onEdit(program)}
            className="flex items-center justify-center gap-1.5 flex-1 px-2 py-2 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-600 hover:text-foreground-900 text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap"
            title="Editar programa"
          >
            <i className="ri-pencil-line text-xs"></i>
            <span className="hidden sm:inline">Editar</span>
          </button>
          <button
            onClick={() => onShare(program)}
            className="flex items-center justify-center gap-1.5 flex-1 px-2 py-2 rounded-xl bg-background-100 hover:bg-sky-100 text-foreground-600 hover:text-sky-700 text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap"
            title="Compartir programa"
          >
            <i className="ri-share-line text-xs"></i>
            <span className="hidden sm:inline">Compartir</span>
          </button>
          <button
            onClick={() => onPromote(program)}
            className="flex items-center justify-center gap-1.5 flex-1 px-2 py-2 rounded-xl bg-background-100 hover:bg-amber-100 text-foreground-600 hover:text-amber-700 text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap"
            title="Promocionar programa"
          >
            <i className="ri-megaphone-line text-xs"></i>
            <span className="hidden sm:inline">Promo</span>
          </button>
          <button
            onClick={() => onNotify(program)}
            className="flex items-center justify-center gap-1.5 flex-1 px-2 py-2 rounded-xl bg-background-100 hover:bg-accent-100 text-foreground-600 hover:text-accent-700 text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap"
            title="Notificar inicio de programa"
          >
            <i className="ri-notification-3-line text-xs"></i>
            <span className="hidden sm:inline">Notificar</span>
          </button>
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex items-center justify-center w-9 h-9 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-500 hover:text-foreground-700 cursor-pointer transition-colors shrink-0"
              title="Más acciones"
            >
              <i className="ri-more-2-fill text-sm"></i>
            </button>
            {menuOpen && (
              <div className="absolute right-0 bottom-full mb-1.5 w-48 bg-background-50 rounded-xl border border-background-200 shadow-lg z-50 py-1 animate-fade-in-up">
                <button
                  onClick={() => { setMenuOpen(false); onCommunity(program); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-team-line text-emerald-500"></i>
                  Ver comunidad
                </button>
                <button
                  onClick={() => { setMenuOpen(false); onStats(program); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-bar-chart-2-line text-violet-500"></i>
                  Ver estadísticas
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}